<script setup lang="ts">
import type { VoiceInfo } from '../../../stores/providers/provider'

import { errorMessageFrom } from '@moeru/std'
import { FieldCheckbox, FieldCombobox } from '@proj-airi/ui'
import { computed, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { TestDummyMarker } from '../../gadgets'

const props = defineProps<{
  // Input fields
  defaultText?: string
  availableVoices: VoiceInfo[]
  /**
   * Selection owned by the caller, so the page can persist it. Omit the prop to
   * keep the choice local to this preview.
   */
  voice?: string

  // Provider-specific handlers (provided from parent)
  generateSpeech: (input: string, voice: string, useSSML: boolean) => Promise<ArrayBuffer>

  // Current state
  apiKeyConfigured?: boolean
  voicesLoading?: boolean
}>()

const emit = defineEmits<{
  'update:voice': [voice: string]
}>()

const { t } = useI18n()

// Playground state
const testText = ref(props.defaultText || 'Hello! This is a test of the voice synthesis.')
const isGenerating = ref(false)
const audioUrl = ref('')
const errorMessage = ref('')
const audioPlayer = ref<HTMLAudioElement | null>(null)
const useSSML = ref(false)
const ssmlText = ref('')
const selectedVoice = ref(props.voice ?? '')

// The catalog decides which entries exist. A selection the catalog no longer
// offers falls back to the first entry, so a deleted voice cannot leave the
// preview on an ID the API rejects. A listed selection is kept, which is what
// makes a saved voice survive a reopen.
watch(
  () => props.availableVoices,
  (newVoices) => {
    if (newVoices.length === 0)
      return
    const current = selectedVoice.value
    if (current && newVoices.some(voice => voice.id === current))
      return
    selectedVoice.value = newVoices[0]?.id || ''
  },
  { immediate: true },
)

// A bound selection is the caller's state, so it travels in both directions:
// a page reload restores the preview, and a user pick reaches the page. An
// unbound playground keeps the selection to itself.
watch(selectedVoice, (value) => {
  if (props.voice !== undefined)
    emit('update:voice', value)
})

watch(() => props.voice, (value) => {
  if (value !== undefined && value !== selectedVoice.value)
    selectedVoice.value = value
})

// A catalog can hold the same display name for many languages, so the option
// label carries the locale code. The option slot renders the name and the code
// separately, as a badge.
const voiceNameById = computed(() => new Map(props.availableVoices.map(voice => [voice.id, voice.name])))

const voiceLocaleById = computed(() => new Map(props.availableVoices.map((voice) => {
  const codes = voice.languages.map(language => language.code.toUpperCase()).filter(code => code !== 'UND')
  return [voice.id, codes.join(' ')]
})))

const voiceOptions = computed(() => {
  return props.availableVoices.map((voice) => {
    const locale = voiceLocaleById.value.get(voice.id) ?? ''

    return {
      value: voice.id,
      label: locale ? `${voice.name} · ${locale}` : voice.name,
    }
  })
})

// Function to generate speech
async function handleGenerateTestSpeech() {
  if ((!testText.value.trim() && !useSSML.value) || (useSSML.value && !ssmlText.value.trim()) || !selectedVoice.value)
    return

  isGenerating.value = true
  errorMessage.value = ''

  try {
    // Stop any currently playing audio
    if (audioUrl.value) {
      stopTestAudio()
    }

    const input = useSSML.value ? ssmlText.value : testText.value

    const response = await props.generateSpeech(input, selectedVoice.value, useSSML.value)

    // Convert the response to a blob and create an object URL
    audioUrl.value = URL.createObjectURL(new Blob([response]))

    // Play the audio
    setTimeout(() => {
      if (audioPlayer.value) {
        audioPlayer.value.play()
      }
    }, 100)
  }
  catch (error) {
    console.error('Error generating speech:', error)
    errorMessage.value = errorMessageFrom(error) ?? 'An unknown error occurred'
  }
  finally {
    isGenerating.value = false
  }
}

// Function to stop audio playback
function stopTestAudio() {
  if (audioPlayer.value) {
    audioPlayer.value.pause()
    audioPlayer.value.currentTime = 0
  }

  // Clean up the object URL to prevent memory leaks
  if (audioUrl.value) {
    URL.revokeObjectURL(audioUrl.value)
    audioUrl.value = ''
  }
}

// Clean up when component is unmounted
onUnmounted(() => {
  if (audioUrl.value) {
    URL.revokeObjectURL(audioUrl.value)
  }
})

// Expose public methods and state
defineExpose({
  testText,
  ssmlText,
  useSSML,
  selectedVoice,
  isGenerating,
  audioUrl,
  errorMessage,
  audioPlayer,
  generateTestSpeech: handleGenerateTestSpeech,
  stopTestAudio,
})
</script>

<template>
  <div w-full rounded-xl>
    <h2 class="mb-4 text-lg text-neutral-500 md:text-2xl dark:text-neutral-400" w-full>
      <div class="inline-flex items-center gap-4">
        <TestDummyMarker />
        <div>
          {{ t('settings.pages.providers.provider.elevenlabs.playground.title') }}
        </div>
      </div>
    </h2>
    <div flex="~ col gap-4">
      <FieldCheckbox
        v-model="useSSML"
        :label="t('settings.pages.modules.speech.sections.section.voice-settings.use-ssml.label')"
        :description="t('settings.pages.modules.speech.sections.section.voice-settings.use-ssml.description')"
      />

      <template v-if="!useSSML">
        <textarea
          v-model="testText"
          :placeholder="t('settings.pages.providers.provider.elevenlabs.playground.fields.field.input.placeholder')"
          border="neutral-100 dark:neutral-800 solid 2 focus:neutral-200 dark:focus:neutral-700"
          transition="all duration-250 ease-in-out"
          bg="neutral-100 dark:neutral-800 focus:neutral-50 dark:focus:neutral-900"
          h-24 w-full rounded-lg px-3 py-2 text-sm outline-none
        />
      </template>
      <template v-else>
        <textarea
          v-model="ssmlText"
          :placeholder="t('settings.pages.modules.speech.sections.section.voice-settings.input-ssml.placeholder')"
          border="neutral-100 dark:neutral-800 solid 2 focus:neutral-200 dark:focus:neutral-700"
          transition="all duration-250 ease-in-out"
          bg="neutral-100 dark:neutral-800 focus:neutral-50 dark:focus:neutral-900"
          h-48 w-full rounded-lg px-3 py-2 text-sm font-mono outline-none
        />
      </template>

      <FieldCombobox
        v-model="selectedVoice"
        :options="voiceOptions"
        :label="t('settings.pages.providers.provider.elevenlabs.playground.fields.field.voice.label')"
        :description="t('settings.pages.providers.provider.elevenlabs.playground.fields.field.voice.description')"
        layout="horizontal"
      >
        <template #option="{ option }">
          <span class="truncate">{{ voiceNameById.get(String(option.value)) ?? option.label }}</span>
          <span
            v-if="voiceLocaleById.get(String(option.value))"
            class="shrink-0 rounded bg-neutral-200 px-1.5 py-0.5 text-[10px] text-neutral-700 font-semibold tracking-wide dark:bg-neutral-700 dark:text-neutral-200"
          >{{ voiceLocaleById.get(String(option.value)) }}</span>
        </template>
      </FieldCombobox>

      <!-- Playground actions -->
      <button
        border="neutral-800 dark:neutral-200 solid 2" transition="border duration-250 ease-in-out"
        rounded-lg px-3 text="neutral-100 dark:neutral-900" py-1.5 text-sm
        :disabled="isGenerating || voicesLoading || (!testText.trim() && !useSSML) || (useSSML && !ssmlText.trim()) || !selectedVoice || !apiKeyConfigured"
        :class="{ 'opacity-50 cursor-not-allowed': isGenerating || voicesLoading || (!testText.trim() && !useSSML) || (useSSML && !ssmlText.trim()) || !selectedVoice || !apiKeyConfigured }"
        bg="neutral-700 dark:neutral-300" @click="handleGenerateTestSpeech"
      >
        <div flex="~ row" items-center gap-2>
          <div i-solar:play-circle-bold-duotone />
          <span>{{ isGenerating ? t('settings.pages.providers.provider.elevenlabs.playground.buttons.button.test-voice.generating') : t('settings.pages.providers.provider.elevenlabs.playground.buttons.button.test-voice.label') }}</span>
        </div>
      </button>
      <!-- Error messages -->
      <div v-if="!apiKeyConfigured" class="mt-2 text-sm text-red-500">
        {{ t('settings.pages.providers.provider.elevenlabs.playground.validation.error-missing-api-key') }}
      </div>
      <div v-if="voicesLoading || !selectedVoice" class="mt-2 text-sm text-red-500">
        {{ voicesLoading ? t('settings.pages.modules.speech.sections.section.playground.select-voice.loading') : t('settings.pages.modules.speech.sections.section.playground.select-voice.required') }}
      </div>
      <div v-if="errorMessage" class="mt-2 text-sm text-red-500">
        {{ errorMessage }}
      </div>
      <audio v-if="audioUrl" ref="audioPlayer" :src="audioUrl" controls class="mt-2 w-full" />
    </div>
    <!-- Slot for additional provider-specific UI in the playground -->
    <slot />
  </div>
</template>
